import React, { useState, useMemo } from 'react';

interface CostItem {
  id: string;
  name: string;
  unitCost: number;
  costType: 'per_person' | 'per_group';
  quantity: number;
}

const defaultCostItems: CostItem[] = [
  { id: '1', name: 'Autobus', unitCost: 3500, costType: 'per_group', quantity: 1 },
  { id: '2', name: 'Santo Stefano', unitCost: 200, costType: 'per_person', quantity: 1 },
  { id: '3', name: 'Guida Roccacalscio + Campo Imperatore + Avvistamento Lupi', unitCost: 1000, costType: 'per_group', quantity: 1 },
  { id: '4', name: 'Arrosticini', unitCost: 20, costType: 'per_person', quantity: 1 },
  { id: '5', name: 'Opi Hotel: Cena + Dormire + Colazione', unitCost: 60, costType: 'per_person', quantity: 1 },
  { id: '6', name: 'Porchetta e Degustazione Vini', unitCost: 20, costType: 'per_person', quantity: 1 },
  { id: '7', name: 'Campeggio x3 gg', unitCost: 40, costType: 'per_person', quantity: 3 },
  { id: '8', name: 'Attività Costa Trabocchi', unitCost: 1000, costType: 'per_group', quantity: 1 },
];

// Helper: calculate all values for a given participant count
function calcForParticipants(
  costItems: CostItem[],
  p: number,
  influencerCostPP: number,
  discountPct: number,
  markupPct: number,
  taxPct: number,
) {
  // Step 1: Trip costs
  const itemTotals = costItems.map(item => ({
    ...item,
    total: item.costType === 'per_person'
      ? item.unitCost * item.quantity * p
      : item.unitCost * item.quantity,
  }));
  const totalTripCosts = itemTotals.reduce((sum, it) => sum + it.total, 0);

  // Step 2: Cost per person (base)
  const basePricePerPerson = totalTripCosts / p;

  // Step 3: + Influencer
  const totalInfluencerCost = influencerCostPP * p;
  const costPerPersonWithInfluencer = basePricePerPerson + influencerCostPP;

  // Step 4: Total real costs (what we MUST cover)
  const totalRealCosts = totalTripCosts + totalInfluencerCost;
  const realCostPerPerson = costPerPersonWithInfluencer;

  // Step 5: Apply markup → desired revenue (what we WANT to actually receive)
  const desiredRevenuePerPerson = realCostPerPerson * (1 + markupPct / 100);
  const desiredRevenueTotal = desiredRevenuePerPerson * p;

  // Step 6: Discount compensation → sticker price (what the customer SEES on the website)
  // Customer pays: stickerPrice × (1 - discount%)
  // We want: stickerPrice × (1 - discount%) = desiredRevenuePerPerson
  // So: stickerPrice = desiredRevenuePerPerson / (1 - discount%)
  const discountFactor = 1 - discountPct / 100;
  const stickerPricePerPerson = discountFactor > 0
    ? desiredRevenuePerPerson / discountFactor
    : desiredRevenuePerPerson;
  const stickerPriceTotal = stickerPricePerPerson * p;

  // Step 7: What the customer ACTUALLY pays (after discount)
  const customerPaysPerPerson = stickerPricePerPerson * discountFactor;
  const customerPaysTotal = customerPaysPerPerson * p;
  // This should equal desiredRevenuePerPerson / desiredRevenueTotal

  // Step 8: Discount amount (what the customer "saves")
  const discountAmountPerPerson = stickerPricePerPerson - customerPaysPerPerson;
  const discountAmountTotal = discountAmountPerPerson * p;

  // Step 9: Profit
  const grossProfit = customerPaysTotal - totalRealCosts;
  const grossProfitPerPerson = grossProfit / p;

  // Step 10: Taxes
  const taxes = grossProfit * (taxPct / 100);
  const netProfit = grossProfit - taxes;
  const netProfitPerPerson = netProfit / p;

  return {
    itemTotals,
    totalTripCosts,
    basePricePerPerson,
    totalInfluencerCost,
    costPerPersonWithInfluencer,
    totalRealCosts,
    realCostPerPerson,
    desiredRevenuePerPerson,
    desiredRevenueTotal,
    discountFactor,
    stickerPricePerPerson,
    stickerPriceTotal,
    customerPaysPerPerson,
    customerPaysTotal,
    discountAmountPerPerson,
    discountAmountTotal,
    grossProfit,
    grossProfitPerPerson,
    taxes,
    netProfit,
    netProfitPerPerson,
  };
}

const TripCostCalculator: React.FC = () => {
  const [tripName, setTripName] = useState('Viaggio 7 giorni');
  const [costItems, setCostItems] = useState<CostItem[]>(defaultCostItems);
  const [participants, setParticipants] = useState(15);
  const [influencerName, setInfluencerName] = useState('Mauro');
  const [influencerCostPerPerson, setInfluencerCostPerPerson] = useState(85);
  const [discountPercent, setDiscountPercent] = useState(10);
  const [customSimParticipants, setCustomSimParticipants] = useState(18);
  const [markupPercent, setMarkupPercent] = useState(30);
  const [taxPercent, setTaxPercent] = useState(50);

  const calculations = useMemo(() => {
    if (participants <= 0) return null;
    return calcForParticipants(costItems, participants, influencerCostPerPerson, discountPercent, markupPercent, taxPercent);
  }, [costItems, participants, influencerCostPerPerson, discountPercent, markupPercent, taxPercent]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const getItemTotal = (item: CostItem, numParticipants: number): number => {
    if (item.costType === 'per_person') {
      return item.unitCost * item.quantity * numParticipants;
    }
    return item.unitCost * item.quantity;
  };

  const addCostItem = () => {
    const newId = String(Date.now());
    setCostItems([
      ...costItems,
      { id: newId, name: '', unitCost: 0, costType: 'per_person', quantity: 1 },
    ]);
  };

  const removeCostItem = (id: string) => {
    setCostItems(costItems.filter(item => item.id !== id));
  };

  const updateCostItem = (id: string, field: keyof CostItem, value: string | number) => {
    setCostItems(costItems.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl p-6 text-white shadow-lg">
        <h2 className="text-2xl font-bold flex items-center gap-3">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          Calcolatore Costi Viaggio
        </h2>
        <p className="mt-1 text-amber-100">Calcola costi, prezzi e guadagni per i tuoi viaggi</p>
      </div>

      {/* Settings Panel - Sticky */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sticky top-0 z-20">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          Parametri
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome Viaggio</label>
            <input
              type="text"
              value={tripName}
              onChange={e => setTripName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              N° Partecipanti
            </label>
            <input
              type="number"
              min={1}
              value={participants}
              onChange={e => setParticipants(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm font-semibold text-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Influencer
            </label>
            <input
              type="text"
              value={influencerName}
              onChange={e => setInfluencerName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Costo Influencer / persona
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">€</span>
              <input
                type="number"
                min={0}
                step={0.01}
                value={influencerCostPerPerson}
                onChange={e => setInfluencerCostPerPerson(parseFloat(e.target.value) || 0)}
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sconto Partecipanti (%)
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={99}
                step={0.1}
                value={discountPercent}
                onChange={e => setDiscountPercent(Math.min(99, parseFloat(e.target.value) || 0))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Rincaro Guadagno (%)
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                step={0.1}
                value={markupPercent}
                onChange={e => setMarkupPercent(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tasse (%)
            </label>
            <div className="relative">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={taxPercent}
                onChange={e => setTaxPercent(Math.min(100, parseFloat(e.target.value) || 0))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Items */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            Voci di Costo — {tripName}
          </h3>
          <button
            onClick={addCostItem}
            className="inline-flex items-center gap-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Aggiungi Voce
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-600 w-1/3">Voce</th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-600">Costo Unitario (€)</th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-600">Tipo</th>
                <th className="text-left py-3 px-2 text-sm font-semibold text-gray-600">Quantità</th>
                <th className="text-right py-3 px-2 text-sm font-semibold text-gray-600">
                  Totale ({participants} pers.)
                </th>
                <th className="py-3 px-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {costItems.map((item, index) => (
                <tr key={item.id} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                  <td className="py-2 px-2">
                    <input
                      type="text"
                      value={item.name}
                      onChange={e => updateCostItem(item.id, 'name', e.target.value)}
                      placeholder="Nome voce..."
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={item.unitCost}
                      onChange={e => updateCostItem(item.id, 'unitCost', parseFloat(e.target.value) || 0)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    />
                  </td>
                  <td className="py-2 px-2">
                    <select
                      value={item.costType}
                      onChange={e => updateCostItem(item.id, 'costType', e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                    >
                      <option value="per_person">A persona</option>
                      <option value="per_group">Per gruppo</option>
                    </select>
                  </td>
                  <td className="py-2 px-2">
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={e => updateCostItem(item.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      title="Numero di giorni/unità"
                    />
                  </td>
                  <td className="py-2 px-2 text-right font-medium text-sm text-gray-800">
                    {calculations ? formatCurrency(getItemTotal(item, participants)) : '—'}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      onClick={() => removeCostItem(item.id)}
                      className="text-red-400 hover:text-red-600 transition-colors p-1"
                      title="Rimuovi voce"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {calculations && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-amber-50">
                  <td colSpan={4} className="py-3 px-2 text-sm font-bold text-gray-800">
                    TOTALE COSTI VIAGGIO
                  </td>
                  <td className="py-3 px-2 text-right font-bold text-lg text-amber-700">
                    {formatCurrency(calculations.totalTripCosts)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Detailed Calculation Breakdown */}
      {calculations && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Dettaglio Calcoli — {participants} Partecipanti
          </h3>

          <div className="space-y-6">
            {/* Step 1: Base Costs */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-5">
              <h4 className="font-semibold text-blue-800 mb-3 text-base">
                1. Costi Base del Viaggio
              </h4>
              <div className="space-y-1 mb-3">
                {calculations.itemTotals.map(item => (
                  <div key={item.id} className="flex justify-between text-sm text-blue-700">
                    <span>
                      {item.name}
                      <span className="text-blue-400 ml-2">
                        ({item.costType === 'per_person' ? 'a persona' : 'per gruppo'}
                        {item.quantity > 1 ? ` × ${item.quantity}` : ''})
                      </span>
                    </span>
                    <span className="font-medium">{formatCurrency(item.total)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-blue-200 pt-2 flex justify-between font-bold text-blue-900">
                <span>Totale Costi Viaggio</span>
                <span>{formatCurrency(calculations.totalTripCosts)}</span>
              </div>
            </div>

            {/* Step 2: Price per Person */}
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-5">
              <h4 className="font-semibold text-indigo-800 mb-3 text-base">
                2. Prezzo Base per Persona
              </h4>
              <div className="text-sm text-indigo-700 space-y-1">
                <div className="flex justify-between">
                  <span>Totale Costi ÷ {participants} partecipanti</span>
                  <span>{formatCurrency(calculations.totalTripCosts)} ÷ {participants}</span>
                </div>
              </div>
              <div className="border-t border-indigo-200 pt-2 mt-2 flex justify-between font-bold text-indigo-900">
                <span>Prezzo Base / Persona</span>
                <span>{formatCurrency(calculations.basePricePerPerson)}</span>
              </div>
            </div>

            {/* Step 3: Influencer */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-5">
              <h4 className="font-semibold text-purple-800 mb-3 text-base">
                3. Costo Influencer ({influencerName})
              </h4>
              <div className="text-sm text-purple-700 space-y-1">
                <div className="flex justify-between">
                  <span>Costo influencer per persona</span>
                  <span>{formatCurrency(influencerCostPerPerson)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Costo influencer totale ({participants} × {formatCurrency(influencerCostPerPerson)})</span>
                  <span>{formatCurrency(calculations.totalInfluencerCost)}</span>
                </div>
              </div>
              <div className="border-t border-purple-200 pt-2 mt-2 flex justify-between font-bold text-purple-900">
                <span>Costo Reale / Persona (viaggio + influencer)</span>
                <span>{formatCurrency(calculations.realCostPerPerson)}</span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {formatCurrency(calculations.basePricePerPerson)} + {formatCurrency(influencerCostPerPerson)} = {formatCurrency(calculations.realCostPerPerson)}
              </div>
            </div>

            {/* Step 4: Total Real Costs */}
            <div className="rounded-lg border border-red-200 bg-red-50 p-5">
              <h4 className="font-semibold text-red-800 mb-3 text-base">
                4. Costi Reali Totali (da coprire)
              </h4>
              <div className="text-sm text-red-700 space-y-1">
                <div className="flex justify-between">
                  <span>Costi viaggio</span>
                  <span>{formatCurrency(calculations.totalTripCosts)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Costi influencer</span>
                  <span>{formatCurrency(calculations.totalInfluencerCost)}</span>
                </div>
              </div>
              <div className="border-t border-red-200 pt-2 mt-2 flex justify-between font-bold text-red-900 text-lg">
                <span>COSTI REALI TOTALI</span>
                <span>{formatCurrency(calculations.totalRealCosts)}</span>
              </div>
              <div className="text-xs text-red-500 mt-1">
                {formatCurrency(calculations.realCostPerPerson)} / persona
              </div>
            </div>

            {/* Step 5: Markup */}
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-5">
              <h4 className="font-semibold text-emerald-800 mb-3 text-base">
                5. Rincaro {markupPercent}% — Ricavo Desiderato
              </h4>
              <p className="text-xs text-emerald-600 mb-3">
                Applichiamo il {markupPercent}% di rincaro sui costi reali. Questo è il ricavo che VOGLIAMO effettivamente incassare da ogni partecipante.
              </p>
              <div className="text-sm text-emerald-700 space-y-1">
                <div className="flex justify-between">
                  <span>Costo reale / persona × (1 + {markupPercent}%)</span>
                  <span>{formatCurrency(calculations.realCostPerPerson)} × {(1 + markupPercent / 100).toFixed(2)}</span>
                </div>
              </div>
              <div className="border-t border-emerald-200 pt-2 mt-2 flex justify-between font-bold text-emerald-900">
                <span>Costo a persona dopo rincaro</span>
                <span>{formatCurrency(calculations.desiredRevenuePerPerson)}</span>
              </div>
              <div className="flex justify-between font-bold text-emerald-900 text-lg mt-2">
                <span>COSTO TOTALE DOPO RINCARO</span>
                <span>{formatCurrency(calculations.desiredRevenueTotal)}</span>
              </div>
              <div className="text-xs text-emerald-500 mt-1">
                {formatCurrency(calculations.desiredRevenuePerPerson)} × {participants} partecipanti = {formatCurrency(calculations.desiredRevenueTotal)}
              </div>
            </div>

            {/* Step 6: Discount Compensation */}
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-5">
              <h4 className="font-semibold text-orange-800 mb-3 text-base">
                6. Compensazione Sconto {discountPercent}% — Prezzo di Listino
              </h4>
              <p className="text-xs text-orange-600 mb-3">
                Il cliente riceve uno sconto del {discountPercent}%. Il prezzo di listino è gonfiato in modo che DOPO lo sconto, 
                il cliente paghi esattamente il ricavo desiderato ({formatCurrency(calculations.desiredRevenuePerPerson)}).
              </p>
              <div className="text-sm text-orange-700 space-y-1">
                <div className="flex justify-between">
                  <span>Formula: Ricavo Desiderato ÷ (1 - {discountPercent}%)</span>
                  <span>{formatCurrency(calculations.desiredRevenuePerPerson)} ÷ {calculations.discountFactor.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Maggiorazione per compensare lo sconto</span>
                  <span className="font-medium">{formatCurrency(calculations.discountAmountPerPerson)} / persona</span>
                </div>
              </div>
              <div className="border-t border-orange-200 pt-2 mt-2 flex justify-between font-bold text-orange-900 text-lg">
                <span>COSTO PER PERSONA DOPO RINCARO E SCONTO</span>
                <span>{formatCurrency(calculations.stickerPricePerPerson)}</span>
              </div>
              <div className="flex justify-between font-bold text-orange-900 text-lg mt-2">
                <span>PREZZO TOTALE DOPO RINCARO E SCONTO</span>
                <span>{formatCurrency(calculations.stickerPriceTotal)}</span>
              </div>
              <div className="text-xs text-orange-500 mt-1">
                {formatCurrency(calculations.stickerPricePerPerson)} × {participants} partecipanti = {formatCurrency(calculations.stickerPriceTotal)}
              </div>
            </div>

            {/* Step 7: What customer actually pays */}
            <div className="rounded-lg border border-teal-200 bg-teal-50 p-5">
              <h4 className="font-semibold text-teal-800 mb-3 text-base">
                7. Prezzo Pagato dal Cliente (dopo sconto {discountPercent}%)
              </h4>
              <p className="text-xs text-teal-600 mb-3">
                Il cliente vede {formatCurrency(calculations.stickerPricePerPerson)} sul sito, 
                riceve {discountPercent}% di sconto, e paga effettivamente:
              </p>
              <div className="text-sm text-teal-700 space-y-1">
                <div className="flex justify-between">
                  <span>Prezzo listino × (1 - {discountPercent}%)</span>
                  <span>{formatCurrency(calculations.stickerPricePerPerson)} × {calculations.discountFactor.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Sconto applicato per persona</span>
                  <span className="text-red-600">- {formatCurrency(calculations.discountAmountPerPerson)}</span>
                </div>
              </div>
              <div className="border-t border-teal-200 pt-2 mt-2 flex justify-between font-bold text-teal-900 text-lg">
                <span>CLIENTE PAGA / PERSONA</span>
                <span>{formatCurrency(calculations.customerPaysPerPerson)}</span>
              </div>
              <div className="text-xs text-teal-500 mt-1">
                Totale incassato: {formatCurrency(calculations.customerPaysTotal)}
              </div>
            </div>

            {/* Step 8: Profit */}
            <div className="rounded-lg border border-green-200 bg-green-50 p-5">
              <h4 className="font-semibold text-green-800 mb-3 text-base">
                8. Guadagni Reali
              </h4>
              <div className="text-sm text-green-700 space-y-2">
                <div className="flex justify-between">
                  <span>Totale Incassato (ricavi effettivi)</span>
                  <span className="font-medium">{formatCurrency(calculations.customerPaysTotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>− Costi Reali Totali</span>
                  <span className="text-red-600 font-medium">- {formatCurrency(calculations.totalRealCosts)}</span>
                </div>
                <div className="border-t border-green-200 pt-2">
                  <div className="flex justify-between font-bold">
                    <span>Guadagno Lordo</span>
                    <span>{formatCurrency(calculations.grossProfit)}</span>
                  </div>
                  <div className="flex justify-between text-xs text-green-600 mt-1">
                    <span>({formatCurrency(calculations.grossProfitPerPerson)} / persona)</span>
                  </div>
                </div>
                <div className="border-t border-green-200 pt-2">
                  <div className="flex justify-between">
                    <span>Tasse ({taxPercent}%)</span>
                    <span className="text-red-600 font-medium">- {formatCurrency(calculations.taxes)}</span>
                  </div>
                </div>
                <div className="border-t-2 border-green-300 pt-2">
                  <div className="flex justify-between font-bold text-green-900 text-lg">
                    <span>GUADAGNO NETTO</span>
                    <span>{formatCurrency(calculations.netProfit)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-green-700 mt-1">
                    <span>Guadagno Netto / Persona</span>
                    <span className="font-bold">{formatCurrency(calculations.netProfitPerPerson)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {calculations && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
            <p className="text-blue-100 text-sm font-medium">Costi Reali</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(calculations.totalRealCosts)}</p>
            <p className="text-blue-200 text-xs mt-1">{formatCurrency(calculations.realCostPerPerson)} / persona</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-5 text-white shadow-lg">
            <p className="text-orange-100 text-sm font-medium">Prezzo di Listino</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(calculations.stickerPricePerPerson)}</p>
            <p className="text-orange-200 text-xs mt-1">Il cliente vede questo</p>
          </div>
          <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-5 text-white shadow-lg">
            <p className="text-teal-100 text-sm font-medium">Cliente Paga</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(calculations.customerPaysPerPerson)}</p>
            <p className="text-teal-200 text-xs mt-1">Dopo sconto {discountPercent}%</p>
          </div>
          <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-lg">
            <p className="text-amber-100 text-sm font-medium">Totale Incassato</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(calculations.customerPaysTotal)}</p>
            <p className="text-amber-200 text-xs mt-1">Ricavi effettivi</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg">
            <p className="text-green-100 text-sm font-medium">Guadagno Netto</p>
            <p className="text-2xl font-bold mt-1">{formatCurrency(calculations.netProfit)}</p>
            <p className="text-green-200 text-xs mt-1">{formatCurrency(calculations.netProfitPerPerson)} / persona</p>
          </div>
        </div>
      )}

      {/* Scenario Simulator: Fixed price, variable participants */}
      {calculations && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Simulazione: Prezzo fisso per {participants}, quanti partecipanti reali?
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Il prezzo sul sito è fissato a <strong className="text-amber-700">{formatCurrency(calculations.stickerPricePerPerson)}</strong> (calcolato per {participants} partecipanti).
            Il cliente paga <strong className="text-teal-700">{formatCurrency(calculations.customerPaysPerPerson)}</strong> dopo lo sconto {discountPercent}%.
            Vediamo cosa succede se vengono più o meno persone.
          </p>
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Partecipanti personalizzati:</label>
            <input
              type="number"
              min={1}
              value={customSimParticipants}
              onChange={e => setCustomSimParticipants(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-24 px-3 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm font-semibold text-purple-700"
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-3 font-semibold text-gray-600">Voce</th>
                  {[
                    Math.max(1, participants - 5),
                    participants,
                    participants + 5,
                    participants + 10,
                    participants + 15,
                    customSimParticipants,
                  ].map((p, idx) => (
                    <th
                      key={`${p}-${idx}`}
                      className={`text-right py-3 px-3 font-semibold ${
                        p === participants && idx !== 5 ? 'text-amber-700 bg-amber-50' :
                        idx === 5 ? 'text-purple-700 bg-purple-50' : 'text-gray-600'
                      }`}
                    >
                      {p} pers.
                      {p === participants && idx !== 5 && <div className="text-xs font-normal text-amber-500">(base)</div>}
                      {idx === 5 && <div className="text-xs font-normal text-purple-500">(custom)</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Fixed price from the base calculation
                  const fixedCustomerPays = calculations.customerPaysPerPerson;
                  const fixedStickerPrice = calculations.stickerPricePerPerson;

                  const scenarioCounts = [
                    Math.max(1, participants - 5),
                    participants,
                    participants + 5,
                    participants + 10,
                    participants + 15,
                    customSimParticipants,
                  ];

                  const scenarios = scenarioCounts.map(p => {
                    // Real costs for this number of participants
                    const totalTrip = costItems.reduce((sum, item) => sum + getItemTotal(item, p), 0);
                    const totalInfluencer = influencerCostPerPerson * p;
                    const realCosts = totalTrip + totalInfluencer;
                    const realCostPP = realCosts / p;

                    // Revenue is fixed price × actual participants
                    const totalRevenue = fixedCustomerPays * p;

                    // Profit
                    const grossProfit = totalRevenue - realCosts;
                    const taxes = grossProfit > 0 ? grossProfit * (taxPercent / 100) : 0;
                    const netProfit = grossProfit - taxes;
                    const netProfitPP = netProfit / p;
                    const marginPercent = realCosts > 0 ? (grossProfit / realCosts) * 100 : 0;

                    return {
                      realCosts,
                      realCostPP,
                      totalRevenue,
                      grossProfit,
                      taxes,
                      netProfit,
                      netProfitPP,
                      marginPercent,
                    };
                  });

                  const rows: { label: string; values: number[]; bold?: boolean; highlight?: boolean; separator?: boolean; isNegativeRed?: boolean; isPercent?: boolean }[] = [
                    { label: 'Prezzo listino / persona', values: scenarioCounts.map(() => fixedStickerPrice) },
                    { label: 'Cliente paga / persona', values: scenarioCounts.map(() => fixedCustomerPays) },
                    { label: 'Costi reali totali', values: scenarios.map(s => s.realCosts), separator: true },
                    { label: 'Costo reale / persona', values: scenarios.map(s => s.realCostPP) },
                    { label: 'Totale incassato', values: scenarios.map(s => s.totalRevenue), bold: true, separator: true },
                    { label: 'Guadagno lordo', values: scenarios.map(s => s.grossProfit), bold: true, isNegativeRed: true },
                    { label: 'Margine %', values: scenarios.map(s => s.marginPercent), isPercent: true, isNegativeRed: true },
                    { label: `Tasse (${taxPercent}%)`, values: scenarios.map(s => -s.taxes), isNegativeRed: true },
                    { label: 'GUADAGNO NETTO', values: scenarios.map(s => s.netProfit), bold: true, highlight: true, isNegativeRed: true },
                    { label: 'Guadagno netto / persona', values: scenarios.map(s => s.netProfitPP), bold: true, isNegativeRed: true },
                  ];

                  return rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className={`border-b border-gray-100 ${
                        row.highlight ? 'bg-green-50' : row.separator ? 'border-t-2 border-t-gray-300' : idx % 2 === 0 ? 'bg-gray-50' : ''
                      }`}
                    >
                      <td className={`py-2 px-3 ${row.bold ? 'font-bold text-gray-900' : 'text-gray-700'}`}>
                        {row.label}
                      </td>
                      {row.values.map((val, i) => {
                        const isBase = scenarioCounts[i] === participants && i !== scenarioCounts.length - 1;
                        const isCustom = i === scenarioCounts.length - 1;
                        return (
                          <td
                            key={i}
                            className={`py-2 px-3 text-right ${row.bold ? 'font-bold' : 'font-medium'} ${
                              isBase ? 'bg-amber-50' : isCustom ? 'bg-purple-50' : ''
                            } ${row.highlight ? 'text-lg' : ''} ${
                              row.isNegativeRed && val < 0 ? 'text-red-600' : 
                              row.highlight && val > 0 ? 'text-green-700' :
                              isBase ? 'text-amber-700' :
                              isCustom ? 'text-purple-700' : 'text-gray-800'
                            }`}
                          >
                            {row.isPercent ? `${val.toFixed(1)}%` : formatCurrency(val)}
                          </td>
                        );
                      })}
                    </tr>
                  ));
                })()}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Strategia:</strong> Fissa il prezzo per il numero <strong>minimo</strong> di partecipanti che ti aspetti ({participants}).
              Se vengono più persone, il prezzo resta uguale ma i costi fissi si spalmano su più gente → <strong>guadagni di più</strong>.
              Se vengono meno di {participants} persone, rischi di andare in perdita (vedi numeri in rosso).
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default TripCostCalculator;
